import React, { useState, useContext, useEffect } from 'react';
import Loadable from 'react-loadable';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import { Paper, CircularProgress } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import MetricsTable from '../common/table/MetricsTable';
import MetricDetails from './MetricDetails';
import ChartDisplay from './Charting/ChartDisplay';
import ChartMenu from './Charting/ChartMenu';
import { geoList, getYearsWithDatapoints } from '../../helpers';
import config from '../../config';
import { SelectedMetricsContext } from '../../context/SelectedMetricsContext';
import Error from '../common/Error';
import Track from '../../helpers/Analytics';
import {
  DATA_LAYER_TYPE_PAYWALL__NONE,
  DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM,
  DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
  getAccessTypeOnSubscriptionService,
  getUserSubscriptionTierFromCookies,
  dataLayerRawPush
} from '@insiderintelligence/datalayer';

const MetricSearch = config.enableMetricSearch ? (
  Loadable({
    loader: () => import('./MetricSearch/MetricSearch'),
    loading: () => <CircularProgress />
  })
) : (
  <></>
);

const styles = theme => ({
  root: {
    margin: 'auto',
    padding: '0px 0px 5px 5px',
    borderRadius: 25,
    marginBottom: 45,
    boxShadow: '0px 7px 40px -12px rgba(0,0,0,0.2)'
  },
  wrapper: {
    maxWidth: 1230,
    position: 'relative'
  },
  narrow: {
    maxWidth: 1230
  },
  table: {
    minWidth: 1230
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    minWidth: 300
  }
});

const Metric = props => {
  const { classes } = props;
  const { metricIds } = useParams();
  const initialSelectedMetrics = (metricIds || '').split(',').map(id => ({ selected: true, id: id.split('-')[0], geographies: [] }));
  const initialSelectedGeos = (metricIds || '')
    .split(',')
    .filter(id => id.split('-')[1])
    .map(id => {
      var metricId = id.split('-')[0];
      var geoNames = id.split('-')[1] ?? null;
      if (geoNames) {
        return { id: metricId, name: geoNames.split('_') };
      }
      return null;
    });
  const [logarithmic, setLogarithmic] = useState(false);
  const [stacked, setStacked] = useState(false);
  const [fillStacked, setFillStacked] = useState(false);
  const [contextSelectedMetrics, setSelectedMetrics] = useContext(SelectedMetricsContext);
  const selectedMetrics = contextSelectedMetrics && contextSelectedMetrics.length > 0 ? contextSelectedMetrics : initialSelectedMetrics;
  const [selectedYears, setSelectedYears] = useState(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [pageLoadedAnalytics, setPageLoadedAnalytics] = useState(false);

  // dataLayer
  useEffect(() => {
    const userSubscriptionTier = getUserSubscriptionTierFromCookies(typeof document !== 'undefined' ? document.cookie : '');
    const sortedKeywords = (metricIds || '').split(',').sort().join(',');

    dataLayerRawPush({
      accessType: getAccessTypeOnSubscriptionService(userSubscriptionTier),
      paywallType: DATA_LAYER_TYPE_PAYWALL__NONE,
      isAdBlock: false,
      isLocked: false,
      keywords: sortedKeywords,
      unifiedAssetId: `${DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM}metric`,
      section: DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
      userSubscriptionTier
    });

    // NOTE: add `contextSelectedMetrics` to the list of dependencies, if you want to track on-page user-changes of selected metrics
    // but you may need to re-construct the metricIds to include the geographies (e.g. `3298,245-us_ww,16092`), using `contextSelectedMetrics[n].geographies[]`
  }, [metricIds]);

  useEffect(() => {
    if (metricIds && metricIds.split(',').length > 10) {
      let limitMetricIds = metricIds.split(',').slice(0, 10).join(',');
      window.location.href = `/metric/${limitMetricIds}`;
    }
  }, [metricIds]);

  useEffect(() => {
    if (metrics && !pageLoadedAnalytics) {
      Track('Pro-PremiumForecast-Asset', { metrics: metrics.map(m => m.name).join(', ') }, true);
      setPageLoadedAnalytics(true);
    }
  }, [metrics, pageLoadedAnalytics]);

  const METRIC_QUERY = gql`
    query getMetricsQuery($ids: [Int!], $orderBy: [_MetricOrdering]) {
      Metric: getMetricsQuery(ids: $ids, orderBy: $orderBy) {
        id
        name
        geographies {
          geographyId
          name
        }
        notes {
          value
          geographyId
        }
        methodology {
          value
          geographyId
        }
        datapoints {
          value
          year
          unit
          geographies {
            geographyId
            name
          }
        }
      }
    }
  `;

  const { error, loading, data } = useQuery(METRIC_QUERY, {
    variables: {
      ids: selectedMetrics.map(sm => parseInt(sm.id)),
      orderBy: {
        field: 'name',
        direction: 'ASC'
      }
    }
  });

  useEffect(() => {
    if (data && (!contextSelectedMetrics || contextSelectedMetrics.length === 0)) {
      let selectedData = data.Metric.map(m => {
        var geoLookup = initialSelectedGeos.find(geo => parseInt(geo.id) === m.id);
        var foundGeo = geoLookup ? geoLookup.name : null;
        if (foundGeo) {
          var fullGeoInfo = geoList
            .filter(g => foundGeo.includes(g.shortName) && m.geographies.some(mg => mg.geographyId === g.id))
            .map(g => ({ selected: true, id: g.id, name: g.name }));
          return { selected: true, id: m.id, name: m.name, geographies: fullGeoInfo };
        }

        return { selected: true, id: m.id, name: m.name, geographies: [] };
      });

      setSelectedMetrics(selectedData);
    }
    setMetrics(data && data.Metric);
  }, [data, setSelectedMetrics, contextSelectedMetrics, initialSelectedGeos]);

  const allYears = metrics && getYearsWithDatapoints(metrics);

  return (
    <>
      {config.enableMetricSearch ? (
        <div className={clsx(classes.narrow, classes.root)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <MetricSearch
            selectedMetrics={selectedMetrics}
            onMetricsSelected={selectedMetrics => {
              setAnimationComplete(false);
              setSelectedMetrics(selectedMetrics);
            }}
          />
        </div>
      ) : null}
      {loading && <CircularProgress style={{ position: 'fixed', top: '100px', left: '50%' }} />}
      {error && <Error />}
      {metrics && selectedMetrics && (
        <>
          <Paper className={clsx(classes.narrow, classes.root)}>
            <ChartDisplay
              metrics={metrics}
              logarithmic={logarithmic}
              stacked={stacked}
              fillStacked={fillStacked}
              selectedMetrics={selectedMetrics}
              selectedYears={selectedYears}
              allYears={allYears}
              onAnimationComplete={() => setAnimationComplete(true)}
            />
            <ChartMenu
              logarithmic={logarithmic}
              setLogarithmic={setLogarithmic}
              stacked={stacked}
              setStacked={setStacked}
              fillStacked={fillStacked}
              setFillStacked={setFillStacked}
              animationComplete={animationComplete}
              metricIds={metricIds}
              metricNames={metrics.map(m => m.name).join(', ')}
            />
          </Paper>
          <Paper className={clsx(classes.root, classes.wrapper)}>
            <MetricsTable
              metrics={metrics}
              selectedMetrics={selectedMetrics}
              onMetricsSelected={selectedMetrics => {
                setAnimationComplete(false);
                setSelectedMetrics(selectedMetrics);
              }}
              selectedYears={selectedYears}
              onYearsSelected={setSelectedYears}
              allYears={allYears}
              showGeography
              showDatapoints
              showDownloadButton
              showAllYears
              allowDeletingMetrics
              analyticsPreName={'Metric'}
              showSelectedMetrics={false}
            />
          </Paper>
          {config.enableMetricDetails &&
            selectedMetrics
              .filter(sm => sm.selected)
              .map(sm => metrics.find(m => m.id === sm.id))
              .filter(m => m)
              .map(m => (
                <Paper key={m.id} className={clsx(classes.narrow, classes.root)}>
                  <MetricDetails metric={m} />
                </Paper>
              ))}
        </>
      )}
    </>
  );
};

export default withStyles(styles)(Metric);
